<script lang="ts">
  import { db } from '../../lib/firebase';
  import { authState } from '../../lib/auth.svelte';
  import { collection, addDoc, Timestamp } from 'firebase/firestore';
  import {
    type TicketCategory,
    type TicketPriority,
    TICKET_CATEGORIES,
    TICKET_PRIORITIES,
  } from '../../lib/types';

  // Props
  type Props = {
    onClose: () => void;
    onSuccess: () => void;
  };
  let { onClose, onSuccess }: Props = $props();

  // Form state
  let subject = $state('');
  let description = $state('');
  let category = $state<TicketCategory>('general');
  let priority = $state<TicketPriority>('normal');
  let emailNotifications = $state(true);

  // UI state
  let loading = $state(false);
  let error = $state('');
  let success = $state(false);

  // Validation
  let subjectError = $derived(subject.trim().length === 0 ? 'Subject is required' : '');
  let descriptionError = $derived(description.trim().length === 0 ? 'Description is required' : '');
  let isValid = $derived(subject.trim().length > 0 && description.trim().length > 0);

  async function handleSubmit(e: Event) {
    e.preventDefault();

    if (!isValid || !authState.user) return;

    loading = true;
    error = '';

    try {
      const now = Timestamp.now();
      await addDoc(collection(db, 'support_tickets'), {
        userId: authState.user.uid,
        userEmail: authState.user.email,
        userName: authState.user.displayName || authState.user.email?.split('@')[0] || 'User',
        subject: subject.trim(),
        description: description.trim(),
        category,
        priority,
        status: 'open',
        emailNotifications,
        createdAt: now,
        updatedAt: now,
      });

      success = true;
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Error creating ticket:', err);
      error = err.message || 'Failed to create ticket. Please try again.';
    } finally {
      loading = false;
    }
  }

  function handleOverlayClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }
</script>

<div class="modal-overlay" onclick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="modal-title">
  <div class="modal">
    <div class="modal-header">
      <h2 id="modal-title">Create Support Ticket</h2>
      <button class="close-btn" onclick={onClose} aria-label="Close modal">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    {#if success}
      <div class="success-message">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p>Ticket created successfully!</p>
      </div>
    {:else}
      <form onsubmit={handleSubmit}>
        {#if error}
          <div class="error-banner" role="alert">{error}</div>
        {/if}

        <div class="form-group">
          <label for="subject">Subject *</label>
          <input
            type="text"
            id="subject"
            bind:value={subject}
            placeholder="Brief summary of your issue"
            class:error={subjectError && subject.length > 0}
          />
          {#if subjectError && subject.length > 0}
            <span class="field-error">{subjectError}</span>
          {/if}
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="category">Category</label>
            <select id="category" bind:value={category}>
              {#each TICKET_CATEGORIES as cat}
                <option value={cat.id}>{cat.label}</option>
              {/each}
            </select>
          </div>

          <div class="form-group">
            <label for="priority">Priority</label>
            <select id="priority" bind:value={priority}>
              {#each TICKET_PRIORITIES as p}
                <option value={p.id}>{p.label}</option>
              {/each}
            </select>
          </div>
        </div>

        <div class="form-group">
          <label for="description">Description *</label>
          <textarea
            id="description"
            bind:value={description}
            placeholder="Describe your issue in detail..."
            rows="5"
            class:error={descriptionError && description.length > 0}
          ></textarea>
          {#if descriptionError && description.length > 0}
            <span class="field-error">{descriptionError}</span>
          {/if}
        </div>

        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input type="checkbox" bind:checked={emailNotifications} />
            <span>Email me updates about this ticket</span>
          </label>
        </div>

        <div class="modal-actions">
          <button type="button" class="btn-secondary" onclick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" class="btn-primary" disabled={loading || !isValid}>
            {#if loading}
              <span class="spinner"></span>
              Creating...
            {:else}
              Create Ticket
            {/if}
          </button>
        </div>
      </form>
    {/if}
  </div>
</div>

<style>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 1rem;
  }

  .modal {
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .modal-header h2 {
    margin: 0;
    font-size: 1.25rem;
    color: #333;
  }

  .close-btn {
    background: none;
    border: none;
    padding: 0.5rem;
    cursor: pointer;
    color: #666;
    border-radius: 6px;
    transition: all 0.2s;
  }

  .close-btn:hover {
    background: #f3f4f6;
    color: #333;
  }

  .close-btn svg {
    width: 20px;
    height: 20px;
  }

  form {
    padding: 1.5rem;
  }

  .error-banner {
    background: #fee2e2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    font-size: 0.9rem;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  input[type="text"],
  select,
  textarea {
    width: 100%;
    padding: 0.625rem 0.875rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.9375rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  input[type="text"]:focus,
  select:focus,
  textarea:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  input.error,
  textarea.error {
    border-color: #ef4444;
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }

  .field-error {
    display: block;
    color: #ef4444;
    font-size: 0.8rem;
    margin-top: 0.25rem;
  }

  .checkbox-group {
    margin-top: 1.25rem;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-weight: normal;
  }

  .checkbox-label input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #667eea;
  }

  .modal-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
  }

  .btn-secondary,
  .btn-primary {
    padding: 0.625rem 1.25rem;
    border-radius: 8px;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-secondary {
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;
  }

  .btn-secondary:hover:not(:disabled) {
    background: #f9fafb;
  }

  .btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  .btn-primary:disabled,
  .btn-secondary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .success-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1.5rem;
    text-align: center;
  }

  .success-message svg {
    width: 64px;
    height: 64px;
    color: #22c55e;
    margin-bottom: 1rem;
  }

  .success-message p {
    font-size: 1.125rem;
    color: #333;
    margin: 0;
  }

  @media (max-width: 480px) {
    .form-row {
      grid-template-columns: 1fr;
    }

    .modal-actions {
      flex-direction: column-reverse;
    }

    .btn-secondary,
    .btn-primary {
      width: 100%;
      justify-content: center;
    }
  }
</style>
